import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

// Angular PrimeNG modules
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { InputSwitchModule } from 'primeng/inputswitch';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { TreeModule } from 'primeng/tree';
import { TreeNode } from 'primeng/api';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { SkeletorComponent } from '../../components/skeletor/skeletor.component';

// Components
import { SelectComponent } from '../../components/select/select.component';
import { TreeSelectorComponent } from '../../components/tree-selector/tree-selector.component';

// Services
import { ConfigurationService } from '../../services/configuration/configuration.service';
import { DominioService } from '../../services/dominio/dominio.service';
import { BusinessPartnerService } from '../../services/business-partner/business-partner.service';
import { DataPersistenceService } from '../../services/data-persistence/data-persistence.service';
import { EmpresaService } from '../../services/empresa/empresa.service';
import { SnackBarService } from '../../components/snack-bar/snackBar.service';
import { ExternalFormService } from '../../services/supplier/external-form.service';
import { ExternalFormConfig, TokenExpiredResult } from '../../_models/external-form.model';

// Models
import { Configuration, Card, CardSection, DataRequest } from '../../_models/configuration.model';
import { ValorDominio } from '../../_models/dominio.model';
import { 
  CreateSupplierForm, 
  RegisterBusinessPartnerRequest, 
  SendEmailRequest,
  DEFAULT_CREATE_SUPPLIER_FORM,
  CreateSupplierMode,
  EmpresaScmInfo
} from './create-supplier.model';

// Utils
import { isValidRut, formatRut, cleanRut, getRutNumber, getRutVerifier } from '../../_utils/rut.util';

@Component({
  selector: 'app-create-supplier',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    InputSwitchModule,
    CardModule,
    DialogModule,
    TreeModule,
    OverlayPanelModule,
    SkeletorComponent,
    SelectComponent,
    TreeSelectorComponent
  ],
  templateUrl: './create-supplier.component.html',
  styleUrls: ['./create-supplier.component.scss']
})
export class CreateSupplierComponent implements OnInit, OnDestroy {
  // Form data
  formData: CreateSupplierForm = { ...DEFAULT_CREATE_SUPPLIER_FORM };
  
  // Modo de operación del componente
  @Input() mode: CreateSupplierMode = 'invitation';
  
  // UI State
  loading: boolean = false;
  loadingEmpresaInfo: boolean = false;
  sendInvitation: boolean = true; // Controla la vista actual
  
  // Estado para bloquear campos cuando se encuentra información de empresa
  empresaFieldsLocked: boolean = false;
  empresaInfoFound: boolean = false;
  
  // Estado de validación de RUT
  rutError: string = '';
  showRutError: boolean = false;
  
  // Estado para controlar el botón de submit mientras se valida el RUT con el WS
  rutValidationComplete: boolean = false;
  
  // Estado para mostrar mensaje cuando no se encuentra información en SII
  siiInfoNotFound: boolean = false;
  
  // Estado para mostrar mensaje de éxito
  showSuccessMessage: boolean = false;
  
  // Validación de token de invitación
  validatingToken: boolean = false;
  invitationUserId: string | null = null;
  
  // Estado para token externo (registro público)
  externalTokenId: string | null = null;
  externalFormConfig: ExternalFormConfig | null = null;
  isExternalTokenValid: boolean = false;
  isExternalTokenExpired: boolean = false;
  externalTokenErrorMessage: string = '';
  
  // Constante del código de autorización estático
  
  // Configuration
  configuration: Configuration | null = null;
  availableCards: ValorDominio[] = [];
  selectedCard: Card | null = null;
  
  // Selector options
  availableRegions: ValorDominio[] = [];
  availableClassifications: ValorDominio[] = [];
  availableBanks: ValorDominio[] = [];
  availableAccountTypes: ValorDominio[] = [];
  availableSizes: ValorDominio[] = [];
  
  // Tree nodes para selectores jerárquicos (productos)
  productTreeNodes: TreeNode[] = [];

  empresaInfo: EmpresaScmInfo = {
    rut: '',
    razonSocial: '',
    nombre: '',
    tamano: '',
    actividadEconomica: ''
  };
  
  // Destroy subject for cleanup
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private configurationService: ConfigurationService,
    private dominioService: DominioService,
    private businessPartnerService: BusinessPartnerService,
    private dataPersistenceService: DataPersistenceService,
    private empresaService: EmpresaService,
    private snackBarService: SnackBarService,
    private externalFormService: ExternalFormService
  ) {}

  ngOnInit(): void {
    // Si es modo embebido, no validar token
    if (this.mode === 'embedded') {
      this.loadConfiguration();
      this.loadDominios();
      return;
    }

    // Verificar si es modo externo (token en la URL)
    this.route.paramMap.subscribe(params => {
      const tokenId = params.get('tokenId');
      if (tokenId) {
        this.externalTokenId = tokenId;
        this.loadExternalFormConfig(tokenId);
        return;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Getters
  get companyId(): string {
    // otherwise use cookie
    return this.dataPersistenceService.getCompanyIdCookie();
  }

  get userId(): string {
    // Si viene companyId por URL (invitation), usarlo
    if (this.invitationUserId) {
      return this.invitationUserId;
    }
    // otherwise use cookie
    return this.dataPersistenceService.getUsername();
  }

  get isInvitationValid(): boolean {
    return !!this.formData.contactName && !!this.formData.contactEmail && 
          !!this.formData.clasificacionProveedor &&
          !!this.formData.rutEmpresa &&
           this.formData.contactEmail.includes('@');
  }

  /**
   * Determina si es un registro externo (sin autenticación)
   */
  get isExternalMode(): boolean {
    return this.mode === 'invitation';
  }

  get isFormValid(): boolean {
    // El botón debe estar bloqueado hasta que la validación del RUT esté completa
    if (!this.rutValidationComplete) {
      return false;
    }
    
    // Validar campos requeridos de las 3 secciones
    const identificationValid = !!this.formData.rutEmpresa && 
                                 !!this.formData.razonSocial &&
                                 !!this.formData.tipoRelacion;
    
    const dataValid = !!this.formData.direccion && 
                      !!this.formData.cobertura &&
                      !!this.formData.clasificacionProveedor &&
                      !!this.formData.fono &&
                      !!this.formData.mail &&
                      this.formData.productosServicios.length > 0 &&
                      !!this.formData.cuentas[0]?.banco &&
                      !!this.formData.cuentas[0]?.tipo &&
                      !!this.formData.cuentas[0]?.numero;
    
    return identificationValid && dataValid;
  }


  /**
   * Carga la configuración del formulario externo para un token específico
   * @param tokenId Token de acceso temporal
   */
  private loadExternalFormConfig(tokenId: string): void {
    this.loading = true;
    this.validatingToken = true;

    this.externalFormService.getExternalFormConfig(tokenId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Verificar si el token está expirado
          if ('expired' in response && response.expired) {
            this.isExternalTokenExpired = true;
            this.externalTokenErrorMessage = response.message;
            this.isExternalTokenValid = false;
            this.loading = false;
            this.validatingToken = false;
            return;
          }

          // Token válido - cargar configuración
          const config = response as ExternalFormConfig;
          this.externalFormConfig = config;
          this.isExternalTokenValid = true;
          this.isExternalTokenExpired = false;
          
          // Cargar configuración desde el formulario externo
          this.loadConfigurationFromExternal(config);
          
          this.loading = false;
          this.validatingToken = false;
        },
        error: (error) => {
          console.error('Error loading external form config:', error);
          this.isExternalTokenValid = false;
          this.isExternalTokenExpired = true;
          this.externalTokenErrorMessage = 'Error al cargar el formulario. Por favor, intente más tarde.';
          this.loading = false;
          this.validatingToken = false;
        }
      });
  }

  /**
   * Carga la configuración recibida del formulario externo
   * @param config Configuración del formulario externo
   */
  private loadConfigurationFromExternal(configuration: ExternalFormConfig): void {
    if (configuration.config) {
      this.configuration = configuration.config as Configuration;
      if (this.configuration?.cards) {
        this.buildCardsOptions(this.configuration);
      }
      if (this.configuration?.supplierClassification) {
        this.buildClassificationsOptions(this.configuration);
      }
    }

    if(configuration.regiones){
      this.availableRegions = configuration.regiones.valores;
    }

    if(configuration.bancos){
      this.availableBanks = configuration.bancos.valores;
    }

    if(configuration.tipoCuenta){
      this.availableAccountTypes = configuration.tipoCuenta.valores;
    }

    if(configuration.productos){
      this.buildProductTreeFromDominio(configuration.productos);
    }

    if(configuration.tamano){
      this.availableSizes = [{
        codigo: configuration.tamano,
        valor: configuration.tamano,
        descripcion: '',
        padreId: ''
      }];
    }

    this.formData.mail = configuration.email;
    this.formData.rutEmpresa = configuration.rut;
    this.formData.razonSocial = configuration.razonSocial;
    this.formData.nombreComercial = configuration.nombreComercial;
    this.formData.tamano = configuration.tamano;
    this.formData.actividadEconomica = configuration.actividadEconomica;
    this.formData.clasificacionProveedor = configuration.clase;
    this.formData.tipoRelacion = configuration.tipoRelacion;
    this.invitationUserId = configuration.userId;
    
    if (this.configuration?.cards) {
      this.selectedCard = this.configuration.cards.find(c => c.cardName === this.formData.tipoRelacion) || null;
    }
  }

  // ========== CONFIGURATION ==========

  /**
   * Carga la configuración de la empresa
   */
  private loadConfiguration(): void {
    this.loading = true;
    
    this.configurationService.getByCompanyId(this.companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (config) => {
          this.configuration = config;
          this.buildCardsOptions(config);
          this.buildClassificationsOptions(config);
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading configuration:', error);
          this.loading = false;
        }
      });
  }

  /**
   * Construye las opciones de tarjetas desde la configuración
   */
  private buildCardsOptions(config: Configuration): void {
    if (config.cards && config.cards.length > 0) {
      this.availableCards = config.cards
        .filter(card => card.enabled)
        .map(card => ({
          codigo: card.cardId,
          valor: card.cardName,
          descripcion: card.cardDescription || '',
          padreId: ''
        }));
    }
  }

  /**
   * Construye las opciones de clasificaciones desde la configuración
   */
  private buildClassificationsOptions(config: Configuration): void {
    if (config.supplierClassification && config.supplierClassification.length > 0) {
      this.availableClassifications = this.flattenClassifications(config.supplierClassification)
        .map(cls => ({
          codigo: cls.classId,
          valor: cls.className,
          descripcion: cls.classDescription || '',
          padreId: cls.parentId || ''
        }));
    }
  }

  /**
   * Aplana la jerarquía de clasificaciones
   */
  private flattenClassifications(classifications: any[]): any[] {
    let result: any[] = [];
    classifications.forEach(cls => {
      result.push(cls);
      if (cls.childs && cls.childs.length > 0) {
        result = result.concat(this.flattenClassifications(cls.childs));
      }
    });
    return result;
  }

  /**
   * Carga los dominios necesarios para los selectores
   */
  private loadDominios(): void {
    // Regiones
    this.dominioService.getDominio('regiones')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (dominio) => {
          this.availableRegions = dominio.valores || [];
        },
        error: (error) => console.error('Error loading regiones:', error)
      });

    // Bancos
    this.dominioService.getDominio('bancos')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (dominio) => {
          this.availableBanks = dominio.valores || [];
        },
        error: (error) => console.error('Error loading bancos:', error)
      });

    // Tipos de cuenta
    this.dominioService.getDominio('tipoCuenta')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (dominio) => {
          this.availableAccountTypes = dominio.valores || [];
        },
        error: (error) => console.error('Error loading tipos cuenta:', error)
      });

    // Productos desde dominios/productos
    this.dominioService.getDominio('productos')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (dominio) => {
          this.buildProductTreeFromDominio(dominio);
        },
        error: (error) => {
          console.error('Error loading productos:', error);
        }
      });

    // Tamaños
    this.dominioService.getDominio('tamano')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (dominio) => {
          this.availableSizes = dominio.valores || [];
        },
        error: (error) => console.error('Error loading tamanos:', error)
      });

  }

  /**
   * Construye el árbol de productos desde un dominio
   */
  private buildProductTreeFromDominio(dominio: any): void {
    if (dominio?.valores && dominio.valores.length > 0) {
      this.productTreeNodes = this.buildTreeFromValorDominio(dominio.valores);
    } else {
    }
  }

  /**
   * Construye árbol desde ValorDominio
   */
  private buildTreeFromValorDominio(valores: ValorDominio[]): TreeNode[] {
    if (!valores || valores.length === 0) return [];

    const nodeMap = new Map<string, ValorDominio>();
    valores.forEach(valor => nodeMap.set(valor.codigo, valor));

    const createTreeNode = (valor: ValorDominio): TreeNode => ({
      key: valor.codigo,
      label: valor.valor,
      data: valor.descripcion,
      children: []
    });

    const treeNodes = valores.map(valor => createTreeNode(valor));

    treeNodes.forEach(node => {
      const valor = nodeMap.get(node.key!);
      if (valor && valor.padreId && valor.padreId !== '0') {
        const parentNode = treeNodes.find(n => n.key === valor.padreId);
        if (parentNode) {
          if (!parentNode.children) parentNode.children = [];
          parentNode.children.push(node);
        }
      }
    });

    return treeNodes.filter(node => {
      const valor = nodeMap.get(node.key!);
      return !valor || !valor.padreId || valor.padreId === '0';
    });
  }



  // ========== EVENTOS ==========

  /**
   * Maneja el cambio del checkbox de invitación
   */
  onSendInvitationToggle(): void {
    this.sendInvitation = this.formData.sendInvitation;
  }

  /**
   * Maneja la selección de clasificación
   */
  onClasificacionChange(value: string | string[]): void {
    this.formData.clasificacionProveedor = Array.isArray(value) ? value[0] : value;
    this.formData.tipoRelacion = this.configuration?.cards.filter(data => data.associatedClassifications.includes(this.formData.clasificacionProveedor))[0].cardName??'';
    if (this.configuration?.cards) {
      this.selectedCard = this.configuration.cards.find(c => c.cardName === this.formData.tipoRelacion) || null;
    }
  }

  /**
   * Maneja la selección de productos/servicios
   */
  onProductsChange(selectedLabels: string[]): void {
    this.formData.productosServicios = selectedLabels;
  }

  /**
   * Envía la invitación
   */
  submitInvitation(): void {
    if (!this.isInvitationValid) {
      console.warn('Por favor complete todos los campos requeridos');
      return;
    }

    const emailData: SendEmailRequest = {
      userId: this.userId,
      emailContacto: this.formData.contactEmail,
      nombreContacto: this.formData.contactName,
      companyId: this.companyId,
      tipoRelacion: this.formData.tipoRelacion,
      clase: this.formData.clasificacionProveedor,
      rut: this.formData.rutEmpresa,
      razonSocial: this.formData.razonSocial,
      nombreComercial: this.formData.nombreComercial,
      tamano: this.formData.tamano,
      actividadEconomica: this.formData.actividadEconomica
    };

    this.loading = true;

    this.businessPartnerService.sendEmailInvitation(emailData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.loading = false;
          console.log('Invitación enviada exitosamente:', response);
          // Mostrar mensaje de éxito y ocultar formulario
          this.showSuccessMessage = true;
        },
        error: (error) => {
          this.loading = false;
          console.error('Error al enviar invitación:', error);
        }
      });
  }

  /**
   * Envía el formulario de registro
   */
  submitForm(): void {
    // Validar RUT antes de enviar
    if (!this.formData.rutEmpresa || !isValidRut(this.formData.rutEmpresa)) {
      this.snackBarService.displaySnackBar(
        'error',
        'RUT Inválido',
        'Por favor ingrese un RUT válido'
      );
      return;
    }

    if (!this.isFormValid) {
      console.warn('Por favor complete todos los campos requeridos');
      return;
    }

    // Mostrar skeleton mientras se procesa el registro
    this.loading = true;

    // Determinar si es modo externo o interno
    if (this.mode === 'invitation' && this.externalTokenId) {
      this.submitExternalForm();
    } else {
      this.submitInternalForm();
    }
  }

  /**
   * Envía el formulario al endpoint externo (para usuarios anónimos)
   */
  private submitExternalForm(): void {
    const registerData = this.buildRegisterData();

    this.externalFormService.submitExternalForm(this.externalTokenId!, registerData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.loading = false;
          console.log('Registro externo exitoso:', response);
          // Mostrar mensaje de éxito y ocultar formulario
          this.showSuccessMessage = true;
        },
        error: (error) => {
          this.loading = false;
          console.error('Error al registrar externamente:', error);
          this.snackBarService.displaySnackBar(
            'error',
            'Error',
            'Error al procesar el registro. Por favor, intente más tarde.'
          );
        }
      });
  }

  /**
   * Envía el formulario al endpoint interno (para usuarios autenticados)
   */
  private submitInternalForm(): void {
    const registerData = this.buildRegisterData();

    this.businessPartnerService.registerBusinessPartner(registerData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Ocultar skeleton
          this.loading = false;
          console.log('Registro exitoso:', response);
          // Mostrar mensaje de éxito y ocultar formulario
          this.showSuccessMessage = true;
        },
        error: (error) => {
          // Ocultar skeleton en caso de error también
          this.loading = false;
          console.error('Error al registrar:', error);
        }
      });
  }

  /**
   * Construye el objeto de datos para el registro
   */
  private buildRegisterData(): RegisterBusinessPartnerRequest {
    return {
      companyId: this.companyId,
      usuarioId: this.userId,
      empresa: {
        rutEmpresa: this.formData.rutEmpresa,
        razonSocial: this.formData.razonSocial,
        tamano: this.formData.tamano || 'Mediana',
        actividadEconomica: this.formData.actividadEconomica || 'Comercio',
        nombreComercial: this.formData.nombreComercial || this.formData.razonSocial,
        direccion: this.formData.direccion,
        cobertura: [this.formData.cobertura],
        fono: this.formData.fono,
        mail: this.formData.mail,
        productosServicios: this.formData.productosServicios,
        cuentas: this.formData.cuentas.map(c => ({
          cuentaId: "cuenta_"+Math.floor(Date.now() / 1000),
          tipoCuenta: c.tipo,
          banco: c.banco,
          numeroCuenta: c.numero
        }))
      },
      socioNegocio: {
        tipoRelacion: this.formData.tipoRelacion,
        clasificacionProveedor: this.formData.clasificacionProveedor,
        ficha: []
      }
    };
  }

  /**
   * Crea otro registro (vuelve a mostrar el formulario)
   */
  createAnother(): void {
    this.showSuccessMessage = false;
    this.resetForm();
  }

  /**
   * Resetea el formulario después de un registro exitoso
   */
  private resetForm(): void {
    this.formData = { ...DEFAULT_CREATE_SUPPLIER_FORM };
    this.empresaFieldsLocked = false;
    this.empresaInfoFound = false;
    this.selectedCard = null;
    // Resetear estados de validación de RUT
    this.rutValidationComplete = false;
    this.siiInfoNotFound = false;
    this.showRutError = false;
    this.rutError = '';
  }

  /**
   * Configura el estado de un campo
   */
  setFieldConfig(sectionId: string, fieldId: string, status: 'required' | 'optional' | 'not_used'): void {
    if (!this.formData.cardFieldsConfig[sectionId]) {
      this.formData.cardFieldsConfig[sectionId] = {};
    }
    this.formData.cardFieldsConfig[sectionId][fieldId] = status;
  }

  /**
   * Obtiene las secciones de la tarjeta seleccionada
   */
  getSelectedCardSections(): CardSection[] {
    if (this.configuration?.general?.cardSections) {
      return this.configuration.general.cardSections.filter(s => s.enabled);
    }
    return [];
  }

  /**
   * Obtiene el estado de configuración de un campo
   */
  getFieldConfigStatus(sectionId: string, fieldId: string): string {
    return this.formData.cardFieldsConfig[sectionId]?.[fieldId] || '';
  }

  /**
   * Obtiene los campos de data request para una sección
   */
  getFieldsForSection(sectionId: string): DataRequest[] {
    if (this.configuration?.dataRequest) {
      return this.configuration.dataRequest.filter(d => d.sectionCardId === sectionId);
    }
    return [];
  }

  /**
   * Maneja la selección de tamaño
   */
  onTamanoChange(value: string | string[]): void {
    this.formData.tamano = Array.isArray(value) ? value[0] : value;
  }

  /**
   * Maneja la selección de actividad económica
   */
  onActividadChange(value: string | string[]): void {
    this.formData.actividadEconomica = Array.isArray(value) ? value[0] : value;
  }

  /**
   * Maneja la selección de cobertura
   */
  onCoberturaChange(value: string | string[]): void {
    this.formData.cobertura = Array.isArray(value) ? value[0] : value;
  }

  /**
   * Maneja la selección de banco
   */
  onBancoChange(value: string | string[]): void {
    this.formData.cuentas[0].banco = Array.isArray(value) ? value[0] : value;
  }

  /**
   * Maneja la selección de tipo cuenta
   */
  onTipoCuentaChange(value: string | string[]): void {
    this.formData.cuentas[0].tipo = Array.isArray(value) ? value[0] : value;
  }

  /**
   * Maneja el cambio en el campo RUT para limpiar errores y resetear validación
   */
  onRutChange(): void {
    // Limpiar error cuando el usuario comienza a escribir
    if (this.showRutError && this.formData.rutEmpresa) {
      this.showRutError = false;
      this.rutError = '';
    }
    // Resetear el estado de validación cuando el usuario cambia el RUT
    this.rutValidationComplete = false;
    this.siiInfoNotFound = false;
  }

  /**
   * Busca información de empresa por RUT al perder el foco del campo
   * Si encuentra información, bloquea los campos correspondientes
   * Si el WS responde 404, muestra leyenda de que no se encontró información en SII
   */
  onRutBlur(): void {
    const rutInput = this.formData.rutEmpresa?.trim();
    
    // Si no hay RUT, limpiar error y mantener botón bloqueado
    if (!rutInput) {
      this.rutError = '';
      this.showRutError = false;
      this.rutValidationComplete = false;
      this.siiInfoNotFound = false;
      return;
    }

    // Validar formato de RUT
    if (!isValidRut(rutInput)) {
      // No es un RUT válido, mostrar error y mantener botón bloqueado
      this.rutError = 'RUT inválido';
      this.showRutError = true;
      this.empresaFieldsLocked = false;
      this.empresaInfoFound = false;
      this.rutValidationComplete = false;
      this.siiInfoNotFound = false;
      return;
    }

    // RUT válido, limpiar error
    this.rutError = '';
    this.showRutError = false;

    // Formatear el RUT (agregar puntos y guión) para mostrar en el input
    this.formData.rutEmpresa = formatRut(rutInput);
    
    // Si ya está bloqueado (ya se encontró información previamente), no buscar de nuevo
    if (this.empresaFieldsLocked) {
      this.rutValidationComplete = true;
      return;
    }

    // Construir RUT con guión sin puntos para enviar al WS (ej: 16767372-4)
    const rutNumber = getRutNumber(rutInput);
    const rutVerifier = getRutVerifier(rutInput);
    const rutForSearch = `${rutNumber}-${rutVerifier}`;
    
    this.loadingEmpresaInfo = true;
    // Inicialmente el botón está bloqueado hasta que el WS responda
    this.rutValidationComplete = false;
    this.siiInfoNotFound = false;

    if(this.mode === 'embedded'){
      this.empresaService.getInfoEmpresaScm(rutForSearch)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (empresaInfo) => {
          // Si encuentra información, cargar y bloquear campos
          if (empresaInfo && empresaInfo.rut) {
            this.empresaInfo = empresaInfo;
            this.formData.rutEmpresa = empresaInfo.rut;
            this.formData.razonSocial = empresaInfo.razonSocial || empresaInfo.nombre;
            this.formData.nombreComercial = empresaInfo.nombre;
            this.formData.tamano = empresaInfo.tamano;
            this.formData.actividadEconomica = empresaInfo.actividadEconomica;
            
            this.empresaFieldsLocked = true;
            this.empresaInfoFound = true;
            this.siiInfoNotFound = false;
          }
          // Habilitar el botón porque el WS respondió con data
          this.rutValidationComplete = true;
          this.loadingEmpresaInfo = false;
        },
        error: (error) => {
          // Si no encuentra información (404), mostrar leyenda y permitir ingreso manual
          console.log('No se encontró información para el RUT:', rutForSearch, 'Error:', error);
          this.empresaFieldsLocked = false;
          this.empresaInfoFound = false;
          this.loadingEmpresaInfo = false;
          
          // Verificar si es un error 404 para mostrar la leyenda de SII
          if (error?.status === 404 || error?.error?.status === 404) {
            this.siiInfoNotFound = true;
          } else {
            this.siiInfoNotFound = true; // También mostramos el mensaje para otros errores
          }
          
          // Habilitar el botón para permitir continuar con el flujo
          this.rutValidationComplete = true;
        }
      });
    }else{
      this.externalFormService.getInfoEmpresaScm(rutForSearch, this.externalTokenId??'')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (empresaInfo) => {
          // Si encuentra información, cargar y bloquear campos
          if (empresaInfo && empresaInfo.rut) {
            this.formData.rutEmpresa = empresaInfo.rut;
            this.formData.razonSocial = empresaInfo.razonSocial || empresaInfo.nombre;
            this.formData.nombreComercial = empresaInfo.nombre;
            this.formData.tamano = empresaInfo.tamano;
            this.formData.actividadEconomica = empresaInfo.actividadEconomica;
            
            this.empresaFieldsLocked = true;
            this.empresaInfoFound = true;
            this.siiInfoNotFound = false;
          }
          // Habilitar el botón porque el WS respondió con data
          this.rutValidationComplete = true;
          this.loadingEmpresaInfo = false;
        },
        error: (error) => {
          // Si no encuentra información (404), mostrar leyenda y permitir ingreso manual
          console.log('No se encontró información para el RUT:', rutForSearch, 'Error:', error);
          this.empresaFieldsLocked = false;
          this.empresaInfoFound = false;
          this.loadingEmpresaInfo = false;
          
          // Verificar si es un error 404 para mostrar la leyenda de SII
          if (error?.status === 404 || error?.error?.status === 404) {
            this.siiInfoNotFound = true;
          } else {
            this.siiInfoNotFound = true; // También mostramos el mensaje para otros errores
          }
          
          // Habilitar el botón para permitir continuar con el flujo
          this.rutValidationComplete = true;
        }
      });
    }
    
  }
}
